import { clsx } from "clsx";
import { type ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Async from "react-select/async";
import { type BaseFieldProps, clearFields, Field, type WrappedFieldProps } from "redux-form";
import { ActionButton, HStack } from "widgets";

import { checkCapability } from "@/modules/users/utils/user.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { FormError } from "@/ui-modules/core/form/layout/FormError.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";
import { FormNote } from "@/ui-modules/core/form/layout/FormNote.js";
import { FormWarning } from "@/ui-modules/core/form/layout/FormWarning.js";
import { VirtualGenomeCreationDialogForm } from "@/ui-modules/entities/genomes/VirtualGenomeCreationDialog.tsx";
import {
  VIRTUAL_GENOME_CREATION_FORM_KEYS,
  VIRTUAL_GENOME_CREATION_FORM_NAME,
} from "@/ui-modules/entities/genomes/VirtualGenomeCreationDialogSettings.js";

import { fieldIsInvalid, fieldIsInWarnState } from "../../../app-utils/form.js";
import type { Organism, User } from "../../../graphql.js";
import { searchSequencedGenomes } from "../../../redux/modules/genome-browser.js";
import type { ReduxDispatch } from "../../../redux/redux-utils.js";
import { CAPABILITY, FILE_BROWSE_TYPES } from "../../../settings/settings.js";
import { SelectItem, SelectWidget } from "../forms/SelectWidget.js";
import { FilePickerField, type IFilePickerFieldProps } from "./FilePickerFormField.js";

export interface IRefGenomeFieldProps extends Partial<WrappedFieldProps> {
  className?: string;
  title?: string | ReactElement;
  tooltip?: string;
  horizontalLayout?: boolean;
  forceWarnState?: string | ReactElement;
  info?: string | ReactElement;
  isDisabled?: boolean;
  onlyExplorerGenome?: boolean;
  filePickerProps?: IFilePickerFieldProps;
}

function RefGenomeField(props: IRefGenomeFieldProps) {
  const [orgIdInput, setOrgIdInput] = useState<boolean>(false);

  // if the initial value is not an array then it might be a select option and we toggle to the corresponding mode
  useEffect(() => {
    if (Array.isArray(props.input.value)) {
      setOrgIdInput(false);
    } else {
      setOrgIdInput(true);
    }
  }, [props.input.value]);

  const toggleMode = () => {
    props.input.onChange(orgIdInput ? [] : null);
    setOrgIdInput(!orgIdInput);
  };

  const { meta, forceWarnState, info } = { ...props };
  return (
    <FormInputGroup horizontalLayout={props.horizontalLayout}>
      {props.title && <FormLabel>{props.title}</FormLabel>}
      <FormInputWidget>
        {!props.onlyExplorerGenome && (
          <div style={{ marginBottom: 5 }}>
            <SelectWidget
              label="Source"
              isUiSmall={true}
              selectionMode="single"
              selectedKeys={[orgIdInput ? "database" : "files"]}
              onSelectionChange={(selection) => {
                if (selection instanceof Set && [...selection][0]) {
                  const selectedKey = [...selection][0];
                  if ((selectedKey === "database" && !orgIdInput) || (selectedKey === "files" && orgIdInput)) {
                    toggleMode();
                  }
                }
              }}
            >
              <SelectItem key="database">Database</SelectItem>
              <SelectItem key="files">Files</SelectItem>
            </SelectWidget>
          </div>
        )}

        {orgIdInput ? (
          <RefGenomeAndSequencesField {...props} />
        ) : (
          <FilePickerField
            isDisabled={props.isDisabled}
            singleSelection={true}
            selectableFileTypes={[FILE_BROWSE_TYPES.DIRECTORY]}
            {...props.filePickerProps}
            value={props.input.value}
            onChange={props.input.onChange}
          />
        )}
        {fieldIsInvalid(props) && <FormError>{props.meta.error}</FormError>}
        {(!!forceWarnState || fieldIsInWarnState(props)) && !fieldIsInvalid(props) && (
          <FormWarning>{forceWarnState ?? meta.warning}</FormWarning>
        )}
        {!!info && <FormNote>{info}</FormNote>}
      </FormInputWidget>
    </FormInputGroup>
  );
}

export function RefGenomeReduxFormField(props: IRefGenomeFieldProps & BaseFieldProps<IRefGenomeFieldProps>) {
  return <Field {...props} component={RefGenomeField} />;
}

function RefGenomeAndSequencesField(props: IRefGenomeFieldProps) {
  const selectClasses = clsx("react-select full-width select2-offscreen", {
    [props.className]: !!props.className,
    error: fieldIsInvalid(props),
    warning: !!props.forceWarnState || fieldIsInWarnState(props),
  });

  const dispatch = useDispatch<ReduxDispatch>();
  const user: User = useSelector<IAppState, User>((state) => state.auth.session);

  const genome: Organism = props.input.value;

  const [showCreateDialog, setShowCreateDialog] = useState(false);

  return (
    <HStack>
      <Async
        className={selectClasses}
        classNamePrefix="react-select"
        loadOptions={(searchTerm: string) =>
          dispatch(searchSequencedGenomes(searchTerm)).then((search) => search.genomes)
        }
        onChange={props.input.onChange}
        value={genome}
        formatOptionLabel={(genome: Organism) => `${genome.name}${genome.species && ` (${genome.species.name})`}`}
        getOptionValue={(genome: Organism) => String(genome.id)}
        placeholder="Select a genome..."
        noOptionsMessage={() => "No matching genome..."}
        // cacheOptions={true} // do not cache, it might prevent the newly created virtual genome to appear in the search result
        isSearchable
        isClearable={true}
        isDisabled={props.isDisabled}
        defaultOptions={true} // tells the control to immediately fire the remote request
        menuPortalTarget={document.body}
        menuPlacement="auto"
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 650,
          }),
        }}
      />
      {checkCapability(user?.capabilities, CAPABILITY.CREATE_VIRTUAL_GENOME) && (
        <ActionButton key="creat-virtual-genome-action" icon="add" onPress={() => setShowCreateDialog(true)} label="" />
      )}
      <VirtualGenomeCreationDialogForm
        isOpen={showCreateDialog}
        onClose={(genome: Organism) => {
          if (genome) {
            props.input?.onChange(genome);
          }

          dispatch(
            clearFields(
              VIRTUAL_GENOME_CREATION_FORM_NAME,
              false,
              false,
              ...Object.values(VIRTUAL_GENOME_CREATION_FORM_KEYS),
            ),
          );

          setShowCreateDialog(false);
        }}
      />
    </HStack>
  );
}
